import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FuzzyProjectSelect from './FuzzyProjectSelect';

const API_URL = process.env.REACT_APP_API_URL || '';

// 获取token
const getToken = () => localStorage.getItem('notes_token') || '';

// fetch with auth
const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'x-token': token
    }
  });
};

export default function MonitorConfig() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    projectId: projectId || '',
    projectName: '',
    scenarios: [],
    timePoints: ['09:00', '11:00', '14:00', '16:00'],
    upgradeNodes: [],
    reportLayout: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');

  // 加载项目列表
  useEffect(() => {
    loadProjects();
  }, []);

  // 加载配置
  useEffect(() => {
    if (selectedProjectId) {
      loadConfig(selectedProjectId);
    } else {
      setLoading(false);
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/projects`);
      const data = await res.json();
      if (data.success) {
        setProjects(data.projects || []);
      }
    } catch (e) {
      console.error('加载项目失败:', e);
    }
  };

  const loadConfig = async (pid) => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${pid}`);
      const data = await res.json();
      if (data.success && data.config) {
        setConfig(data.config);
      } else {
        // 新配置，需要选择项目，同时填入项目名称
        const proj = projects.find(p => p.id === pid);
        setConfig({
          projectId: pid,
          projectName: proj?.name || '',
          scenarios: [],
          timePoints: ['09:00', '11:00', '14:00', '16:00'],
          upgradeNodes: [],
          reportLayout: null
        });
      }
    } catch (e) {
      console.error('加载配置失败:', e);
    }
    setLoading(false);
  };

  const handleProjectChange = (pid) => {
    setSelectedProjectId(pid);
    if (pid) {
      const proj = projects.find(p => p.id === pid);
      setConfig(prev => ({
        ...prev,
        projectId: pid,
        projectName: proj?.name || ''
      }));
    }
  };

  const addScenario = () => {
    setConfig({
      ...config,
      scenarios: [...config.scenarios, { id: `scenario_${Date.now()}`, name: '', baseline_ms: '' }]
    });
  };

  const updateScenario = (idx, field, value) => {
    const newScenarios = [...config.scenarios];
    // baseline_ms转为数字或null
    if (field === 'baseline_ms') {
      newScenarios[idx] = { ...newScenarios[idx], [field]: value === '' ? null : parseInt(value) };
    } else {
      newScenarios[idx] = { ...newScenarios[idx], [field]: value };
    }
    setConfig({ ...config, scenarios: newScenarios });
  };

  const removeScenario = (idx) => {
    const newScenarios = [...config.scenarios];
    newScenarios.splice(idx, 1);
    setConfig({ ...config, scenarios: newScenarios });
  };

  const addTimePoint = () => {
    setConfig({
      ...config,
      timePoints: [...config.timePoints, '']
    });
  };

  const updateTimePoint = (idx, value) => {
    const newTimePoints = [...config.timePoints];
    newTimePoints[idx] = value;
    setConfig({ ...config, timePoints: newTimePoints });
  };

  const removeTimePoint = (idx) => {
    const newTimePoints = [...config.timePoints];
    newTimePoints.splice(idx, 1);
    setConfig({ ...config, timePoints: newTimePoints });
  };

  const addUpgradeNode = () => {
    setConfig({
      ...config,
      upgradeNodes: [...config.upgradeNodes, { date: '', desc: '', detail: '' }]
    });
  };

  const updateUpgradeNode = (idx, field, value) => {
    const newNodes = [...config.upgradeNodes];
    newNodes[idx] = { ...newNodes[idx], [field]: value };
    setConfig({ ...config, upgradeNodes: newNodes });
  };

  const removeUpgradeNode = (idx) => {
    const newNodes = [...config.upgradeNodes];
    newNodes.splice(idx, 1);
    setConfig({ ...config, upgradeNodes: newNodes });
  };

  const handleSave = async () => {
    if (!config.projectId) {
      alert('请选择项目');
      return;
    }
    if (config.scenarios.length === 0) {
      alert('请添加至少一个操作场景');
      return;
    }
    if (config.timePoints.some(tp => !tp)) {
      alert('请填写所有监测时点');
      return;
    }

    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${config.projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await res.json();
      if (data.success) {
        alert('配置已保存');
        navigate('/monitor');
      } else {
        alert(data.error || '保存失败');
      }
    } catch (e) {
      alert('保存失败: ' + e.message);
    }
    setSaving(false);
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="monitor-config">
      <div className="page-header">
        <button onClick={() => navigate('/monitor')} className="btn-back">← 返回</button>
        <h1>⚙️ 监控配置</h1>
      </div>

      <div className="config-form">
        {/* 项目选择 */}
        <div className="form-section">
          <h3>项目信息</h3>
          <div className="form-group">
            <label>选择项目：</label>
            <FuzzyProjectSelect
              projects={projects}
              value={selectedProjectId}
              onChange={handleProjectChange}
              placeholder="请选择项目"
            />
          </div>
          {config.projectName && (
            <div className="form-group">
              <label>项目名称：</label>
              <input type="text" value={config.projectName} disabled />
            </div>
          )}
        </div>

        {/* 操作场景 */}
        <div className="form-section">
          <h3>操作场景</h3>
          <button onClick={addScenario} className="btn-secondary">+ 添加场景</button>
          <div className="scenarios-list">
            {config.scenarios.map((scenario, idx) => (
              <div key={idx} className="scenario-row">
                <input
                  type="text"
                  placeholder="场景名称"
                  value={scenario.name}
                  onChange={e => updateScenario(idx, 'name', e.target.value)}
                  className="input-flex"
                />
                <input
                  type="number"
                  placeholder="基线值(s)"
                  value={scenario.baseline_ms ?? ''}
                  onChange={e => updateScenario(idx, 'baseline_ms', e.target.value)}
                  className="input-small"
                  title="留空则自动按历史中位数计算"
                />
                <span className="input-hint">s</span>
                <button onClick={() => removeScenario(idx)} className="btn-danger-small">删除</button>
              </div>
            ))}
            {config.scenarios.length === 0 && (
              <div className="empty-hint">暂无场景，请添加</div>
            )}
          </div>
        </div>

        {/* 监测时点 */}
        <div className="form-section">
          <h3>监测时点</h3>
          <button onClick={addTimePoint} className="btn-secondary">+ 添加时点</button>
          <div className="timepoints-list">
            {config.timePoints.map((tp, idx) => (
              <div key={idx} className="timepoint-row">
                <input
                  type="time"
                  value={tp}
                  onChange={e => updateTimePoint(idx, e.target.value)}
                />
                <button onClick={() => removeTimePoint(idx)} className="btn-danger-small">删除</button>
              </div>
            ))}
          </div>
        </div>

        {/* 升级节点 */}
        <div className="form-section">
          <h3>升级/迁移节点</h3>
          <button onClick={addUpgradeNode} className="btn-secondary">+ 添加节点</button>
          <div className="upgrade-nodes-list">
            {config.upgradeNodes.map((node, idx) => (
              <div key={idx} className="upgrade-node-row">
                <input
                  type="date"
                  value={node.date}
                  onChange={e => updateUpgradeNode(idx, 'date', e.target.value)}
                  placeholder="日期"
                />
                <input
                  type="text"
                  placeholder="描述"
                  value={node.desc}
                  onChange={e => updateUpgradeNode(idx, 'desc', e.target.value)}
                  className="input-flex"
                />
                <input
                  type="text"
                  placeholder="详情"
                  value={node.detail}
                  onChange={e => updateUpgradeNode(idx, 'detail', e.target.value)}
                  className="input-flex"
                />
                <button onClick={() => removeUpgradeNode(idx)} className="btn-danger-small">删除</button>
              </div>
            ))}
          </div>
        </div>

        {/* 报告布局配置 */}
        <div className="form-section">
          <h3>📐 报告布局</h3>
          <p style={{ fontSize: '11px', color: '#888', marginBottom: '10px' }}>
            选择报告页显示的组件。顺序从上到下排列。
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { key: 'statusCard', label: '📍 今日状态卡片', desc: '各场景响应时间卡片' },
              { key: 'comparisonTable', label: '🔄 迁移前后对比表', desc: '硬件迁移前后的平均响应时长对比' },
              { key: 'weeklyChart', label: '📈 近7天趋势图', desc: '柱状图+在线人数折线图' },
              { key: 'trafficGrid', label: '📊 红绿灯表格', desc: '近7天系统响应速度表格' },
            ].map(item => {
              const checked = !config.reportLayout || config.reportLayout.some(i => i.type === item.key);
              return (
                <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked}
                    onChange={() => {
                      const layout = config.reportLayout || [
                        { type: 'statusCard', config: {} },
                        { type: 'comparisonTable', config: {} },
                        { type: 'weeklyChart', config: { height: 130 } },
                        { type: 'trafficGrid', config: {} },
                      ];
                      const exists = layout.some(i => i.type === item.key);
                      const newLayout = exists
                        ? layout.filter(i => i.type !== item.key)
                        : [...layout, { type: item.key, config: item.key === 'weeklyChart' ? { height: 130 } : {} }];
                      setConfig({ ...config, reportLayout: newLayout });
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '13px' }}>{item.label}</div>
                    <div style={{ fontSize: '11px', color: '#999' }}>{item.desc}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <p style={{ fontSize: '11px', color: '#aaa', marginTop: '8px' }}>
            {(!config.reportLayout || config.reportLayout.length === 0) ? '⚠️ 全部关闭时默认显示所有组件' :
             `已选中 ${config.reportLayout.length} 个组件`}
          </p>
        </div>

        {/* 保存按钮 */}
        <div className="form-actions">
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存配置'}
          </button>
          <button onClick={() => navigate('/monitor')} className="btn-secondary">
            取消
          </button>
        </div>
      </div>
    </div>
  );
}
